import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { GameSummary, SystemDef } from '@replay/shared';
import { api } from '../lib/api.ts';
import { useHints, useNavAction } from '../lib/nav/react.tsx';
import { useAmbient } from '../components/Ambient.tsx';
import { CoverGrid } from '../components/GameCard.tsx';
import { useLocalGames, useOnline } from '../lib/offline.ts';
import { ApiError } from '../lib/api.ts';
import { Icon } from '../components/Icon.tsx';
import { Empty, ErrorState, Loading, useLayerFocus } from '../components/ui.tsx';

const PAGE = 60;
const SORTS = [
  ['title', 'A–Z'],
  ['recent', 'Recién añadidos'],
  ['played', 'Jugados recientemente'],
  ['rating', 'Mejor valorados'],
  ['year', 'Por año'],
] as const;

type Params = ReturnType<typeof useSearchParams>[0];

function FilterSheet({ params, update, genres, onClose, total }: { params: Params; update: (k: string, v: string | null) => void; genres: { name: string; count: number }[]; onClose: () => void; total: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayerFocus(ref);
  const sort = params.get('sort') ?? 'title';
  const toggle = (k: string, v: string) => update(k, params.get(k) === v ? null : v);
  return (
    <>
      <div className="sheet-backdrop" onMouseDown={onClose} />
      <aside className="sheet" role="dialog" aria-label="Filtros y orden" data-nav-scope ref={ref}>
        <div className="row between">
          <h2>Filtros</h2>
          <button className="btn btn-ghost btn-icon btn-sm" aria-label="Cerrar" onClick={onClose} data-nav-close>
            <Icon name="x" />
          </button>
        </div>
        <div>
          <h3>Ordenar</h3>
          <div className="opts" data-nav-row>
            {SORTS.map(([v, label]) => (
              <button key={v} className="chip" aria-pressed={sort === v} onClick={() => update('sort', v)} data-autofocus={sort === v ? '' : undefined}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3>Mostrar</h3>
          <div className="opts" data-nav-row>
            <button className="chip" aria-pressed={params.get('favorites') === '1'} onClick={() => toggle('favorites', '1')}>
              <Icon name="heart" /> Favoritos
            </button>
            <button className="chip" aria-pressed={params.get('hacks') === '1'} onClick={() => toggle('hacks', '1')}>
              <Icon name="hack" /> Hackroms
            </button>
            <button className="chip" aria-pressed={params.get('players') === '2'} onClick={() => toggle('players', '2')}>
              <Icon name="users" /> 2 o más jugadores
            </button>
            <button className="chip" aria-pressed={params.get('online') === '1'} onClick={() => toggle('online', '1')}>
              Juego online
            </button>
          </div>
        </div>
        {genres.length > 0 && (
          <div>
            <h3>Género</h3>
            <div className="opts">
              {genres.map((g) => (
                <button key={g.name} className="chip" aria-pressed={params.get('genre') === g.name} onClick={() => toggle('genre', g.name)}>
                  {g.name} <span className="faint">{g.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="row" style={{ marginTop: 'auto' }}>
          <button className="btn btn-ghost" onClick={() => ['favorites', 'hacks', 'players', 'online', 'genre', 'sort'].forEach((k) => update(k, null))}>
            Limpiar
          </button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={onClose}>
            Ver {total === 1 ? '1 juego' : `${total} juegos`}
          </button>
        </div>
      </aside>
    </>
  );
}

export function Library() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [sheet, setSheet] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  useAmbient(null);
  useHints([
    { button: 'Y', label: 'Favorito' },
    { button: 'X', label: 'Filtros' },
    { button: 'LT', label: 'Consola' },
  ]);

  const systems = useQuery({ queryKey: ['systems'], queryFn: () => api<(SystemDef & { gameCount: number })[]>('/api/systems'), staleTime: 300_000 });
  const genres = useQuery({ queryKey: ['genres'], queryFn: () => api<{ name: string; count: number }[]>('/api/genres'), staleTime: 300_000 });
  const collection = useQuery({ queryKey: ['collection', slug], queryFn: () => api<{ name: string; description: string }>(`/api/collections/${slug}`), enabled: !!slug });

  const update = (k: string, v: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
        next.delete('search');
        return next;
      },
      { replace: true },
    );
  };

  // Debounced search into the URL.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('q') ?? '') !== q) update('q', q || null);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Arriving from the search button: focus the field.
  useEffect(() => {
    if (params.get('search') === '1') setTimeout(() => searchRef.current?.focus(), 50);
  }, [params]);

  const filters = {
    q: params.get('q') ?? undefined,
    system: params.get('system') ?? undefined,
    genre: params.get('genre') ?? undefined,
    players: params.get('players') ?? undefined,
    favorites: params.get('favorites') ?? undefined,
    hacks: params.get('hacks') ?? undefined,
    online: params.get('online') ?? undefined,
    sort: params.get('sort') ?? 'title',
    collection: slug,
  };

  const games = useInfiniteQuery({
    queryKey: ['games', filters],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<{ total: number; games: GameSummary[] }>('/api/games', { query: { ...filters, limit: PAGE, offset: pageParam } }),
    getNextPageParam: (last, all) => (all.length * PAGE < last.total ? all.length * PAGE : undefined),
  });
  // Offline library: games fully downloaded on this device (works without a connection).
  const online = useOnline();
  const localQ = useLocalGames();
  const networkDown = !online || (games.error instanceof ApiError && games.error.kind === 'network');
  const localMode = params.get('local') === '1' || networkDown;
  const localList = (localQ.data ?? []).filter(
    (g) => (!filters.system || g.systemId === filters.system) && (!filters.q || g.title.toLowerCase().includes(filters.q.toLowerCase())),
  );
  const list = localMode ? localList : (games.data?.pages.flatMap((p) => p.games) ?? []);
  const total = games.data?.pages[0]?.total ?? 0;
  const activeSystems = (systems.data ?? []).filter((s) => s.gameCount > 0);
  const extraFilters = ['genre', 'players', 'favorites', 'hacks', 'online'].filter((k) => params.get(k)).length + (filters.sort !== 'title' ? 1 : 0);

  // Infinite scrolling: load the next page as the end of the grid approaches.
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => e[0]?.isIntersecting && games.hasNextPage && !games.isFetchingNextPage && games.fetchNextPage(), { rootMargin: '800px' });
    io.observe(el);
    return () => io.disconnect();
  }, [games]);

  // X opens filters; LT / RT step through consoles.
  useNavAction((a) => {
    if (sheet) return false;
    if (a === 'secondary') {
      setSheet(true);
      return true;
    }
    if (a === 'prevSub' || a === 'nextSub') {
      const ids = [undefined, ...activeSystems.map((s) => s.id)];
      const i = ids.indexOf(filters.system);
      update('system', ids[(i + (a === 'nextSub' ? 1 : -1) + ids.length) % ids.length] ?? null);
      return true;
    }
  });

  return (
    <div className="library">
      <div className="lib-top">
        <div className="lib-title">
          {slug && (
            <Link to="/library" className="btn btn-ghost btn-sm">
              <Icon name="back" /> Biblioteca
            </Link>
          )}
          <h1>{slug ? (collection.data?.name ?? 'Colección') : 'Biblioteca'}</h1>
          {localMode ? <span>{localList.length === 1 ? '1 juego en este dispositivo' : `${localList.length} juegos en este dispositivo`}</span> : games.data && <span>{total === 1 ? '1 juego' : `${total} juegos`}</span>}
        </div>
        {slug && collection.data?.description && <p className="muted prose">{collection.data.description}</p>}
        <div className="lib-controls">
          <label className="searchbox">
            <Icon name="search" />
            <span className="sr-only">Buscar</span>
            <input ref={searchRef} className="input" type="search" placeholder="Buscar por título o estudio" value={q} onChange={(e) => setQ(e.target.value)} enterKeyHint="search" />
          </label>
          <button className="btn btn-ghost" onClick={() => setSheet(true)} aria-haspopup="dialog">
            <Icon name="sliders" /> Filtros{extraFilters ? ` · ${extraFilters}` : ''}
          </button>
          <button className="chip" aria-pressed={localMode} disabled={networkDown} onClick={() => update('local', localMode ? null : '1')}>
            <Icon name="download" /> En este dispositivo
          </button>
          {(extraFilters > 0 || filters.q || filters.system) && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setQ('');
                setParams(new URLSearchParams(), { replace: true });
              }}
            >
              Quitar filtros
            </button>
          )}
        </div>
        {activeSystems.length > 1 && (
          <div className="systems" role="group" aria-label="Consola" data-nav-row>
            <button className="chip" aria-pressed={!filters.system} onClick={() => update('system', null)}>
              Todas
            </button>
            {activeSystems.map((s) => (
              <button key={s.id} className="chip" aria-pressed={filters.system === s.id} onClick={() => update('system', filters.system === s.id ? null : s.id)}>
                {s.name} <span className="faint">{s.gameCount}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {networkDown && (
        <p className="notice" style={{ margin: '0 var(--gutter) var(--s-4)' }}>
          Sin conexión: estos son los juegos descargados en este dispositivo. Puedes jugarlos y tus partidas se subirán al volver Internet.
        </p>
      )}
      {localMode ? (
        localList.length === 0 ? (
          <div className="lib-empty">
            <Empty title={networkDown ? 'No hay juegos descargados en este dispositivo' : 'Todavía no has descargado juegos aquí'}>
              {networkDown
                ? 'Cuando vuelva la conexión, abre la ficha de un juego y pulsa «Descargar para jugar sin conexión».'
                : 'Abre la ficha de un juego y pulsa «Descargar para jugar sin conexión», o simplemente juega: se quedará en este dispositivo.'}
            </Empty>
          </div>
        ) : (
          <CoverGrid games={localList} />
        )
      ) : games.isLoading ? (
        <Loading />
      ) : games.error ? (
        <div className="lib-empty">
          <ErrorState error={games.error} onRetry={() => games.refetch()} />
        </div>
      ) : list.length === 0 ? (
        <div className="lib-empty">
          <Empty
            title={filters.q || extraFilters || filters.system ? 'Nada coincide con esa búsqueda' : 'Aquí todavía no hay juegos'}
            action={
              <Link className="btn" to="/requests">
                <Icon name="inbox" /> Pedir un juego
              </Link>
            }
          >
            {filters.q || extraFilters || filters.system ? 'Prueba con otro nombre o quita algún filtro.' : '¿Echas algo en falta? Pídelo y lo añadiremos.'}
          </Empty>
        </div>
      ) : (
        <>
          <CoverGrid games={list} />
          <div ref={sentinel} />
          {games.hasNextPage && (
            <div className="row" style={{ justifyContent: 'center', paddingBottom: 'var(--s-7)' }}>
              <button className="btn" onClick={() => games.fetchNextPage()} disabled={games.isFetchingNextPage}>
                {games.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
              </button>
            </div>
          )}
        </>
      )}

      {sheet && <FilterSheet params={params} update={update} genres={genres.data ?? []} onClose={() => setSheet(false)} total={total} />}
    </div>
  );
}
